/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { AddCircle, Clear, Delete, Edit, Search } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Grid, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridRowId, GridRowModel, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import useThrowAsyncError from "@/hooks/useThrowAsyncError";

import { Container } from "@/theme/globalStyles";
import DataTable from "@/components/dataTable";
import { useAuth } from "@/context/global";
import DialogNew from "./modal/dialogAdd";
import DialogEdit from "./modal/dialogEdit";
import serviceModeloFatura from "@/services/serviceModeloFatura";
import messages from "@/utils/messages";
import TextInput from "@/components/hookForms/textField";
import ConfirmationDialog from "@/components/confirmationDialog";
import { tipoDadoOptions } from "@/utils/constants";
import AutoComplete from "@/components/hookForms/autoComplete";

const tipoDadoText = {
    T: "Texto",
    N: "Numérico",
    D: "Decimal",
};

const CadastroCampos = () => {
    const { setTitlePage } = useAuth();

    const [openModalAdd, setOpenModalAdd] = useState<boolean>(false);
    const [exclusionLoading, setExclusionLoading] = useState(false);
    const [exclusionState, setExclusionState] = useState({ data: "", open: false });
    const [data, setData] = useState<any>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState(null);
    const [page, setPage] = useState(0);
    const [rowCount, setRowCount] = useState(10);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "",
            sort: "" as GridSortDirection,
        },
    ]);

    useEffect(() => {
        setTitlePage("Captura de Faturas - Cadastro dos Campos");
    }, [setTitlePage]);

    const [pageSize, setPageSize] = useState(100);
    const [editState, setEditState] = useState({
        open: false,
        descricaoChave: "",
        tipoDado: "",
        descricaoFaturaValoresID: 0,
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        defaultValues: {
            descricaoChave: "",
            tipoDado: "",
        },
    });

    const actionColumns = useMemo(
        () => [
            {
                headerName: "Operações",
                field: "operations",
                type: "actions",
                width: 200,
                hide: false,
                getActions: (params: { id: GridRowId; row: GridRowModel }) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Editar">
                                <Edit />
                            </Tooltip>
                        }
                        label="Editar"
                        onClick={() =>
                            setEditState({
                                open: true,
                                descricaoFaturaValoresID: params.row.descricaoFaturaValoresID,
                                descricaoChave: params.row.descricaoChave,
                                tipoDado: params.row.tipoDado,
                            })
                        }
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Excluir">
                                <Delete />
                            </Tooltip>
                        }
                        label="Excluir"
                        onClick={() => setExclusionState({ data: params.id.toString(), open: true })}
                    />,
                ],
            },
        ],
        []
    );

    const columns = useMemo(
        () => [
            {
                headerName: "ID",
                field: "descricaoFaturaValoresID",
                type: "number",
                headerAlign: "left",
                identity: true,
                maxWidth: 110,
                flex: 1,
                id: 70,
            },
            {
                headerName: "Descrição Chave",
                field: "descricaoChave",
                type: "string",
                minWidth: 200,
                flex: 1,
            },
            {
                headerName: "Tipo Dado",
                field: "tipoDado",
                type: "string",
                minWidth: 200,
                flex: 1,
                valueFormatter: (item: any) => {
                    return tipoDadoText[item.value];
                },
            },
            ...actionColumns,
        ],
        [actionColumns]
    );

    useEffect(() => {
        getAllWithFilters(filters);
    }, [page, pageSize, sortModel]);

    const throwAsyncError = useThrowAsyncError();

    const getAllWithFilters = async (filters: any) => {
        setData([]);
        setIsLoading(true);

        serviceModeloFatura
            .getAtributosWithFilter({
                ...filters,
                take: pageSize,
                currentPage: page + 1,
                sortExpression: sortModel[0]?.field ?? "",
                sortDirection: sortModel[0]?.sort ?? "",
            })
            .then((result) => {
                if (!result) {
                    throw new Error("Falha ao carregar os dados.");
                }
                setData(result.data.content);
                setRowCount(result.data.numberOfElements);
            })
            .catch((err) => {
                setData([]);
                setRowCount(0);
                throwAsyncError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleDelete = async () => {
        setExclusionLoading(true);

        serviceModeloFatura
            .deleteAtributo(exclusionState.data)
            .then((result) => {
                if (result) {
                    toast.success(messages.deleteSucess);
                }
            })
            .catch((err) => {
                toast.error(messages.deleteError, err);
            })
            .finally(() => {
                setExclusionLoading(false);
                setExclusionState({ data: "", open: false });
                getAllWithFilters(filters);
            });
    };

    const onClear = () => {
        reset();
        setFilters(null);
        getAllWithFilters(null);
    };

    const onSuccessAction = () => {
        getAllWithFilters(filters);
    };

    const onSubmit = (values: any) => {
        const newFilters = {
            ...values,
        };

        setFilters(newFilters);
        getAllWithFilters(newFilters);
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} mt={2}>
                                <Grid item md={12} xs={12}>
                                    <Grid container spacing={2} display="flex" alignItems="center">
                                        <Grid item xs={12} md={4}>
                                            <TextInput
                                                id="descricaoChave"
                                                name="descricaoChave"
                                                label="Descrição Chave"
                                                control={control}
                                                error={errors}
                                                maxLength={20}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <AutoComplete
                                                id="tipoDado"
                                                name="tipoDado"
                                                label="Tipo Dado"
                                                options={tipoDadoOptions}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <Button
                                                id="pesquisar"
                                                disableElevation
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Search />}
                                                type="submit"
                                            >
                                                Pesquisar
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <Button
                                                id="limpar"
                                                style={{ marginTop: 16, marginBottom: 8 }}
                                                variant="outlined"
                                                color="warning"
                                                startIcon={<Clear />}
                                                onClick={() => onClear()}
                                                disableElevation
                                                fullWidth
                                                size="medium"
                                            >
                                                Limpar
                                            </Button>
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <Button
                                                id="novo"
                                                variant="contained"
                                                color="primary"
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<AddCircle />}
                                                onClick={() => setOpenModalAdd(true)}
                                                disableElevation
                                                fullWidth
                                                size="medium"
                                            >
                                                Novo
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>
                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data ?? []}
                            rowId={(row: GridRowModel) => row.descricaoFaturaValoresID}
                            rowCount={rowCount}
                            isLoading={isLoading}
                            onPageChange={(currentPage: number) => setPage(currentPage)}
                            onPageSizeChange={(currentPageSize: number) => setPageSize(currentPageSize)}
                            pageSize={pageSize}
                            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
                            disableRowSelectionOnClick={true}
                        />
                    </Box>
                </CardContent>
            </Card>
            <DialogNew open={openModalAdd} onSuccess={() => onSuccessAction()} onClose={() => setOpenModalAdd(false)} />

            <DialogEdit
                open={editState.open}
                onSuccess={() => onSuccessAction()}
                data={{
                    open: editState.open,
                    descricaoChave: editState.descricaoChave,
                    tipoDado: editState.tipoDado,
                    descricaoFaturaValoresID: editState.descricaoFaturaValoresID,
                }}
                onClose={() =>
                    setEditState({
                        open: false,
                        descricaoChave: "",
                        tipoDado: "",
                        descricaoFaturaValoresID: 0,
                    })
                }
            />

            <ConfirmationDialog
                open={exclusionState?.open}
                loading={exclusionLoading}
                title={"Excluir Campo da Fatura"}
                message={
                    "Ao excluir o campo da fatura o sistema deixará de capturar o campo selecionado. Deseja realmente excluir o registro?"
                }
                cancelButtonText={"Não"}
                confirmButtonText={"Sim"}
                onClose={() => setExclusionState({ data: "", open: false })}
                onCancel={() => setExclusionState({ data: "", open: false })}
                onConfirm={() => {
                    handleDelete();
                }}
            />

            {/* {openImport && (
                <DialogLoad
                    onClose={() => {
                        setOpenImport(false);
                    }}
                    onSuccess={() => onSuccessAction()}
                />
            )} */}
        </Container>
    );
};

export default CadastroCampos;
